import { CalendarDate } from '../value-objects/CalendarDate';
import { completedYearsOfService } from './TenurePolicy';

/**
 * Lo que se le debe a alguien cuando deja la empresa, según la LFT.
 *
 * **Este archivo calcula dinero que se le paga a una persona real.** Equivocarse
 * aquí no es un número mal pintado: es un error de nómina con consecuencias
 * laborales y fiscales, y quien lo cobre de menos tiene un año para demandarlo.
 * Por eso vive en el dominio, puro y probado, y no dentro de una pantalla.
 *
 * > **Nada de esto sustituye una revisión legal.** Codifica la lectura estándar
 * > de la LFT y **no es asesoría**: los supuestos, los topes y sobre todo qué
 * > procede en cada tipo de separación los tiene que confirmar RR.HH. o quien
 * > lleve lo laboral antes de que alguien pague con este número. Cada concepto
 * > cita su artículo para que esa revisión sea posible sin leer el código.
 *
 * **Todo se calcula en centavos enteros.** Un cálculo de nómina en coma flotante
 * acumula deriva y termina en un centavo que no cuadra con el recibo; los enteros
 * no tienen esa clase de error. Se redondea una vez por concepto, no al final.
 *
 * ## Los dos bloques, que la gente confunde
 *
 * - **Finiquito**: se debe **siempre**, renuncie o lo corran. Es lo que la
 *   persona ya ganó y no ha cobrado.
 * - **Indemnización**: sólo cuando la separación lo amerita. Un despido
 *   injustificado la genera; una renuncia no.
 *
 * Llamarle «finiquito» a todo es el error de captura más común, y produce
 * cálculos que pagan de menos a quien fue despedido.
 */

/**
 * Cómo terminó la relación laboral. **Determina qué se debe**, así que no es una
 * etiqueta descriptiva: es la entrada más consecuente de este archivo.
 */
export type SeparationKind =
  /** Se va por su voluntad. Sin indemnización. */
  | 'renuncia'
  /** Despido con causa comprobada (art. 47). Sin indemnización constitucional. */
  | 'despido-justificado'
  /** Despido sin causa (art. 48). Genera tres meses y prima de antigüedad. */
  | 'despido-injustificado'
  /** Vence un contrato por tiempo determinado. Sin indemnización. */
  | 'fin-de-contrato'
  /** Ambas partes convienen. Lo que se pague de más es acuerdo, no ley. */
  | 'mutuo-acuerdo';

export const SEPARATION_KIND_LABEL: Readonly<Record<SeparationKind, string>> = {
  renuncia: 'Renuncia voluntaria',
  'despido-justificado': 'Despido justificado',
  'despido-injustificado': 'Despido injustificado',
  'fin-de-contrato': 'Fin de contrato',
  'mutuo-acuerdo': 'Mutuo acuerdo',
};

/**
 * Los parámetros que la ley fija como **mínimos** y la empresa puede mejorar, más
 * los que cambian cada año.
 *
 * Configurables y no constantes por dos razones distintas: el aguinaldo y la
 * prima vacacional son pisos que muchas empresas superan por contrato, y el
 * salario mínimo cambia cada 1 de enero — codificarlo obligaría a un despliegue
 * anual para no calcular mal.
 */
export interface SettlementParams {
  /** Días de aguinaldo. Piso legal: 15 (art. 87). */
  readonly aguinaldoDays: number;
  /** Prima vacacional. Piso legal: 0.25 (art. 80). */
  readonly primaVacacionalRate: number;
  /** Salario mínimo general diario vigente, en centavos. Tope de la prima de antigüedad. */
  readonly minimumWageCents: number;
}

export interface SettlementInput {
  /** Salario mensual bruto, en centavos. */
  readonly monthlySalaryCents: number;
  readonly hireDate: CalendarDate;
  /** Último día trabajado. */
  readonly separationDate: CalendarDate;
  readonly kind: SeparationKind;
  /**
   * Días de vacaciones ya generados y **no gozados** al separarse.
   *
   * Entra como dato y no se deriva aquí a propósito: el saldo real de esta
   * persona lo lleva el módulo Time, con su periodo por aniversario, sus días
   * prescritos y sus solicitudes en vuelo. Recalcularlo aquí produciría un
   * segundo número que discreparía del que la persona ve en su portal.
   */
  readonly pendingVacationDays: number;
  /**
   * Días de vacaciones que **sí gozó** del periodo.
   *
   * No se pagan como días —ya los disfrutó— pero **su prima vacacional sí se
   * debe**, salvo que ya se la hayan pagado. Ver `primaPaidForTakenDays`.
   */
  readonly takenVacationDays: number;
  /**
   * Si la prima de los días ya gozados se pagó en su momento.
   *
   * **Es la casilla que decide si el finiquito paga de menos.** El artículo 80
   * manda la prima sobre los días que *corresponden*, no sobre los que sobraron:
   * quien tomó sus quince días y nunca cobró el 25% sigue teniendo derecho a él.
   * Calcularla sólo sobre lo pendiente le quita esa prima a quien sí descansó — y
   * es el error más fácil de cometer, porque el número resultante se ve razonable.
   *
   * Va como dato y no se deduce: el portal no lleva registro de qué primas se
   * pagaron, eso vive en nómina. Preguntarlo es honesto; suponerlo no.
   */
  readonly primaPaidForTakenDays: boolean;
  /**
   * Días de vacaciones que le **corresponden por año**, según su antigüedad.
   *
   * No confundir con `pendingVacationDays`, y la diferencia costó una prueba en
   * rojo antes de salir de aquí: el integrado se calcula sobre el **derecho
   * anual**, no sobre lo que quedó sin gozar. Usar lo pendiente hace que quien
   * ya tomó sus vacaciones cobre menos indemnización que quien no las tomó — un
   * castigo por haber descansado, que además paga por debajo de la ley.
   *
   * Sale de `TenurePolicy.resolveEntitlement`, igual que en el resto del portal.
   */
  readonly annualVacationDays: number;
  /** Días trabajados del último periodo de nómina que no se han pagado. */
  readonly unpaidWorkedDays: number;
  readonly params: SettlementParams;
}

/** Un renglón del cálculo, con lo que hace falta para revisarlo a mano. */
export interface SettlementLine {
  readonly concept: string;
  /** El artículo de la LFT que lo sostiene. Sin esto no se puede auditar. */
  readonly legalBasis: string;
  /** Cuántos días de salario. */
  readonly days: number;
  readonly amountCents: number;
  /** Cómo salió el número, en una línea legible. */
  readonly detail: string;
}

export interface Settlement {
  /** Lo que se debe siempre. */
  readonly finiquito: readonly SettlementLine[];
  /** Lo que sólo se debe según el tipo de separación. Vacío en una renuncia. */
  readonly indemnizacion: readonly SettlementLine[];
  readonly finiquitoTotalCents: number;
  readonly indemnizacionTotalCents: number;
  readonly totalCents: number;
  /** Salario diario, en centavos. */
  readonly dailySalaryCents: number;
  /** Salario diario integrado, en centavos. Base de la indemnización. */
  readonly integratedDailySalaryCents: number;
  readonly yearsOfService: number;
}

/** Redondeo a centavo entero, media unidad hacia arriba. Una vez por concepto. */
const round = (cents: number): number => Math.round(cents);

/**
 * Salario diario (art. 89): el mensual entre **30**, siempre.
 *
 * No entre 30.4 ni entre los días que traiga el mes. La LFT fija el divisor, y
 * usar el real haría que el mismo sueldo produjera finiquitos distintos según el
 * mes en que alguien se va.
 */
export const dailyFromMonthly = (monthlySalaryCents: number): number =>
  monthlySalaryCents / 30;

/**
 * Salario diario integrado (art. 84): el diario más la parte proporcional de las
 * prestaciones que se perciben por año.
 *
 * `SDI = diario × (365 + aguinaldo + vacaciones × prima) / 365`
 *
 * Es la base de la indemnización y de la prima de antigüedad, **no** la del
 * finiquito. Usar el diario para indemnizar paga de menos; usar el integrado
 * para el aguinaldo proporcional paga de más y lo cuenta dos veces.
 */
export const integratedDaily = (
  dailyCents: number,
  /** El derecho **anual**, no lo pendiente. Ver `SettlementInput.annualVacationDays`. */
  annualVacationDays: number,
  params: SettlementParams,
): number => {
  const factor =
    (365 + params.aguinaldoDays + annualVacationDays * params.primaVacacionalRate) / 365;
  return dailyCents * factor;
};

/** Días transcurridos del año en curso hasta la separación, para el proporcional. */
const daysIntoYear = (date: CalendarDate): number => {
  const startOfYear = CalendarDate.fromISOOrThrow(`${String(date.year)}-01-01`);
  return startOfYear.daysUntil(date) + 1;
};

/**
 * Si procede la prima de antigüedad (art. 162).
 *
 * Doce días por año, y **no siempre se paga**: sólo a quien es separado —lo
 * corran con causa o sin ella— y a quien renuncia con **quince años o más** de
 * servicio. Pagarla en toda renuncia es el error caro más frecuente en el otro
 * sentido: regala doce días por año a quien la ley no se los da.
 */
export const seniorityPremiumApplies = (
  kind: SeparationKind,
  yearsOfService: number,
): boolean => {
  if (kind === 'despido-justificado' || kind === 'despido-injustificado') return true;
  if (kind === 'renuncia') return yearsOfService >= 15;
  // Fin de contrato y mutuo acuerdo: lo que se pague es acuerdo, no ley.
  return false;
};

export const calculateSettlement = (input: SettlementInput): Settlement => {
  const { params } = input;
  const daily = dailyFromMonthly(input.monthlySalaryCents);
  const years = completedYearsOfService(input.hireDate, input.separationDate);
  const sdi = integratedDaily(daily, input.annualVacationDays, params);

  const finiquito: SettlementLine[] = [];

  if (input.unpaidWorkedDays > 0) {
    finiquito.push({
      concept: 'Días trabajados pendientes',
      legalBasis: 'LFT art. 82',
      days: input.unpaidWorkedDays,
      amountCents: round(daily * input.unpaidWorkedDays),
      detail: `${String(input.unpaidWorkedDays)} días × salario diario`,
    });
  }

  /*
   * Aguinaldo proporcional (art. 87). Se cuenta desde el 1 de enero y no desde el
   * aniversario laboral: el aguinaldo es por año calendario, a diferencia de las
   * vacaciones, que van por aniversario. Confundir los dos ejes es el error que
   * produce un aguinaldo de más para quien entró en noviembre.
   */
  const workedDaysThisYear = Math.min(
    daysIntoYear(input.separationDate),
    input.hireDate.year === input.separationDate.year
      ? input.hireDate.daysUntil(input.separationDate) + 1
      : Number.POSITIVE_INFINITY,
  );
  const aguinaldoDays = (params.aguinaldoDays * workedDaysThisYear) / 365;
  finiquito.push({
    concept: 'Aguinaldo proporcional',
    legalBasis: 'LFT art. 87',
    days: Number(aguinaldoDays.toFixed(2)),
    amountCents: round(daily * aguinaldoDays),
    detail: `${String(params.aguinaldoDays)} días × ${String(workedDaysThisYear)}/365 del año`,
  });

  /*
   * Los días no gozados se pagan como días (art. 79). Los gozados no: ya los
   * disfrutó.
   */
  if (input.pendingVacationDays > 0) {
    finiquito.push({
      concept: 'Vacaciones no gozadas',
      legalBasis: 'LFT art. 76 y 79',
      days: input.pendingVacationDays,
      amountCents: round(daily * input.pendingVacationDays),
      detail: `${String(input.pendingVacationDays)} días pendientes × salario diario`,
    });
  }

  /*
   * La prima vacacional va sobre los días que **corresponden**, no sobre los que
   * sobraron.
   *
   * Ésta es la corrección que más dinero mueve de este archivo. Calcularla sólo
   * sobre lo pendiente le quita el 25% a quien tomó sus vacaciones y nunca cobró
   * la prima — y el resultado se ve razonable, que es lo que lo hace peligroso.
   *
   * Si nómina ya la pagó por los días gozados, sólo queda la de los pendientes.
   */
  const primaDays =
    input.pendingVacationDays + (input.primaPaidForTakenDays ? 0 : input.takenVacationDays);

  if (primaDays > 0) {
    const percent = String(Math.round(params.primaVacacionalRate * 100));
    finiquito.push({
      concept: 'Prima vacacional',
      legalBasis: 'LFT art. 80',
      days: primaDays,
      amountCents: round(daily * primaDays * params.primaVacacionalRate),
      detail: input.primaPaidForTakenDays
        ? `${percent}% sobre ${String(primaDays)} días pendientes — la de los ${String(input.takenVacationDays)} ya gozados se pagó en su momento`
        : `${percent}% sobre ${String(primaDays)} días que le corresponden (${String(input.pendingVacationDays)} pendientes + ${String(input.takenVacationDays)} ya gozados, cuya prima no se ha pagado)`,
    });
  }

  const indemnizacion: SettlementLine[] = [];

  if (input.kind === 'despido-injustificado') {
    // Art. 48: tres meses, sobre el salario **integrado**.
    indemnizacion.push({
      concept: 'Indemnización constitucional',
      legalBasis: 'LFT art. 48',
      days: 90,
      amountCents: round(sdi * 90),
      detail: '3 meses (90 días) × salario diario integrado',
    });
  }

  if (seniorityPremiumApplies(input.kind, years)) {
    /*
     * Art. 162 fracc. II: el salario base de la prima de antigüedad **se topa a
     * dos veces el salario mínimo**. Es el único tope de este archivo y el que
     * más se olvida — sin él, un sueldo alto multiplica la prima por diez.
     */
    const cap = params.minimumWageCents * 2;
    const base = Math.min(daily, cap);
    const capped = daily > cap;
    indemnizacion.push({
      concept: 'Prima de antigüedad',
      legalBasis: 'LFT art. 162',
      days: 12 * years,
      amountCents: round(base * 12 * years),
      detail: capped
        ? `12 días × ${String(years)} años, con el salario topado a 2 salarios mínimos`
        : `12 días × ${String(years)} años × salario diario`,
    });
  }

  const sum = (lines: readonly SettlementLine[]): number =>
    lines.reduce((total, line) => total + line.amountCents, 0);

  const finiquitoTotalCents = sum(finiquito);
  const indemnizacionTotalCents = sum(indemnizacion);

  return {
    finiquito,
    indemnizacion,
    finiquitoTotalCents,
    indemnizacionTotalCents,
    totalCents: finiquitoTotalCents + indemnizacionTotalCents,
    dailySalaryCents: round(daily),
    integratedDailySalaryCents: round(sdi),
    yearsOfService: years,
  };
};
